import { useMemo, useState } from 'react'

import { dayLabel, thousands } from '@/lib/format'
import type { TimeseriesPoint } from '@/types'

interface Metric {
  key: keyof Omit<TimeseriesPoint, 'date'>
  label: string
}

const METRICS: Metric[] = [
  { key: 'contacts', label: 'New contacts' },
  { key: 'messages', label: 'Messages' },
  { key: 'matches', label: 'Matches found' },
  { key: 'introductions', label: 'Introductions' },
]

/**
 * Four metrics of wildly different magnitude (messages dwarf introductions).
 * Small multiples rather than one plot: each chart keeps its own scale, so no
 * second y-axis is ever needed and no false correlation is implied.
 */
export function TrendGrid({ points }: { points: TimeseriesPoint[] }) {
  return (
    <div className="grid grid-cols-1 gap-px bg-line sm:grid-cols-2 xl:grid-cols-4">
      {METRICS.map((metric) => (
        <div key={metric.key} className="bg-surface p-4">
          <TrendChart points={points} metric={metric} />
        </div>
      ))}
    </div>
  )
}

function TrendChart({ points, metric }: { points: TimeseriesPoint[]; metric: Metric }) {
  const [hover, setHover] = useState<number | null>(null)

  const width = 260
  const height = 72
  const padding = { top: 6, right: 6, bottom: 6, left: 6 }

  const values = useMemo(() => points.map((p) => Number(p[metric.key]) || 0), [points, metric.key])
  const total = values.reduce((sum, value) => sum + value, 0)
  const max = Math.max(...values, 1)

  const plotWidth = width - padding.left - padding.right
  const plotHeight = height - padding.top - padding.bottom
  const step = plotWidth / Math.max(values.length - 1, 1)

  const coords = values.map((value, index) => ({
    x: padding.left + index * step,
    y: padding.top + plotHeight - (value / max) * plotHeight,
  }))

  const line = coords.map((c, i) => `${i === 0 ? 'M' : 'L'}${c.x.toFixed(1)} ${c.y.toFixed(1)}`)
  const area = [
    ...line,
    `L${coords[coords.length - 1]?.x.toFixed(1) ?? 0} ${padding.top + plotHeight}`,
    `L${coords[0]?.x.toFixed(1) ?? 0} ${padding.top + plotHeight}`,
    'Z',
  ]

  const active = hover !== null ? coords[hover] : null

  function handleMove(event: React.MouseEvent<SVGSVGElement>) {
    const rect = event.currentTarget.getBoundingClientRect()
    const x = ((event.clientX - rect.left) / rect.width) * width - padding.left
    const index = Math.round(x / step)
    setHover(index >= 0 && index < values.length ? index : null)
  }

  return (
    <figure className="m-0">
      <figcaption className="flex items-baseline justify-between gap-2">
        <span className="text-xs font-medium text-ink-muted">{metric.label}</span>
        <span className="text-sm font-semibold tabular text-ink">{thousands(total)}</span>
      </figcaption>

      <div className="relative mt-2">
        <svg
          width="100%"
          height={height}
          viewBox={`0 0 ${width} ${height}`}
          preserveAspectRatio="none"
          role="img"
          aria-label={`${metric.label} over the selected period, ${total} in total`}
          onMouseMove={handleMove}
          onMouseLeave={() => setHover(null)}
        >
          {/* Area is a wash, never a saturated block. */}
          <path d={area.join(' ')} fill="var(--series-1)" opacity="0.1" />
          <path
            d={line.join(' ')}
            fill="none"
            stroke="var(--series-1)"
            strokeWidth="2"
            strokeLinecap="round"
            strokeLinejoin="round"
            vectorEffect="non-scaling-stroke"
          />
          {active && (
            <>
              <line
                x1={active.x}
                x2={active.x}
                y1={padding.top}
                y2={padding.top + plotHeight}
                stroke="var(--axis)"
                strokeWidth="1"
                vectorEffect="non-scaling-stroke"
              />
              <circle
                cx={active.x}
                cy={active.y}
                r="4"
                fill="var(--series-1)"
                stroke="var(--surface-1)"
                strokeWidth="2"
              />
            </>
          )}
        </svg>

        {hover !== null && points[hover] && (
          <div
            className="pointer-events-none absolute -top-1 z-10 -translate-y-full whitespace-nowrap rounded border border-line bg-surface px-2 py-1 text-xs shadow-lg"
            style={{
              left: `${(coords[hover].x / width) * 100}%`,
              transform: 'translate(-50%, -100%)',
            }}
          >
            <span className="text-ink-muted">{dayLabel(points[hover].date)}</span>
            <span className="ml-2 font-medium tabular text-ink">{values[hover]}</span>
          </div>
        )}
      </div>
    </figure>
  )
}
